import { PipelineBuildDetails, PipelineBuildDetailsSteps } from "@microtica/ms-ap-sdk";
import { Alert, Badge, Button, Col, Divider, Drawer, Row, Skeleton, Space, Steps } from "antd";
import { Content } from "antd/lib/layout/layout";
import { ExpandAltOutlined, ShrinkOutlined, CloseOutlined, CloseCircleOutlined, LoadingOutlined, MonitorOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { getPipelinesService, getRealtimeService, disconnectRealtimeService } from "../../backend";
import PipelineStatusTag from "../PipelineStatusTag";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import Ansi from "ansi-to-react";

export interface TemplateDeployStatusDrawerProps {
    title: ReactNode;
    footer?: ReactNode;
    visible?: boolean;
    onClose?: () => void;
    templateExecutionId?: string;
    buildId?: string;
    errorMessage?: string;
}

const LOGS_PLACEHOLDER = `⏳ Initializing logs...

`;

export default function TemplatePipelineStatusDrawer(props: TemplateDeployStatusDrawerProps) {
    const currentProject = useRecoilValue(currentProjectState);
    const [socket, setSocket] = useState<Socket>();
    const [pipeline, setPipeline] = useState<PipelineBuildDetails>();
    const [logs, setLogs] = useState(LOGS_PLACEHOLDER);
    const [showDetailedLogs, setShowDetailedLogs] = useState(false);
    const [currentStep, setCurrentStep] = useState<number>();
    const [drawerHeight, setDrawerHeight] = useState("60%");
    const [loading, setLoading] = useState(true);
    const visibleRef = useRef<boolean>(false);
    const logTimestampColor = "#097BB5";
    const logTimestampRegex = /\[(\d+)\]/;
    const localTimezoneOffset = moment().utcOffset();

    useEffect(() => {
        if (props.visible) {
            visibleRef.current = true;
        }
    }, [props.visible]);

    useEffect(() => {
        loadPipeline();
        const interval = setInterval(async () => {
            const status = await loadPipeline();
            if (["SUCCEEDED", "FAILED"].includes(status)) {
                clearInterval(interval);
            }
        }, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.buildId, props.templateExecutionId, props.visible]);

    useEffect(() => {
        let allLogs = "";
        if (socket !== undefined) {
            socket.on("logs", (logs: string) => {
                allLogs = allLogs.concat(logs);
                setLogs(allLogs);
            });
        }
    }, [socket]);

    useEffect(() => {
        if (currentStep !== undefined) {
            handleSelectStep(currentStep);
        }
    }, [showDetailedLogs]);

    async function loadPipeline() {
        if (props.templateExecutionId && props.buildId && props.visible) {
            const { data: pipeline } = await getPipelinesService().getPipelineBuild(
                currentProject!.id,
                props.templateExecutionId,
                props.buildId
            );
            setPipeline(pipeline);
            // props.visible changed his value from false to true (user clicked on View Logs/Pipeline)
            if (visibleRef.current) {
                const failedStepIndex = pipeline.steps.findIndex(step => step.status === "FAILED");
                if (failedStepIndex !== -1 && currentStep !== failedStepIndex) {
                    handleSelectStep(failedStepIndex, pipeline.steps[failedStepIndex]);
                }
                // user clicked on View Logs/Pipeline for the first time and there is no error
                else if (currentStep === undefined) {
                    handleSelectStep(0, pipeline.steps[0]);
                }
                visibleRef.current = false;
            }
            setLoading(false);
            return pipeline.status;
        }
        return "Status unknown";
    }

    function handleClose() {
        props.onClose?.();
        disconnectRealtimeService();
    }

    function getStepStatus(status: string) {
        if (status === "FAILED") {
            return "error";
        } else if (status === "RUNNING") {
            return "process";
        } else if (status === "SUCCEEDED") {
            return "finish";
        } else if (status === "PENDING") {
            return "wait";
        }
    }

    const getStepStatusForDisplay = (status: string) => {
        switch (status) {
            case "FAILED":
                return "Failed";
            case "RUNNING":
                return "In progress";
            case "SUCCEEDED":
                return "Done";
            case "PENDING":
                return "Pending";
        }
    }

    function toggleDrawerHeight() {
        if (drawerHeight === "100%") {
            setDrawerHeight("60%");
        } else {
            setDrawerHeight("100%");
        }
    }

    function handleSelectStep(index: number, stepDetails?: PipelineBuildDetailsSteps) {
        const step = stepDetails || pipeline?.steps[index];
        const socket = getRealtimeService(
            {
                projectId: currentProject!.id,
                pipelineId: props.templateExecutionId,
                buildId: props.buildId,
                stepName: step?.name,
                typeOfLogs: "pipeline",
                codebuildLogs: showDetailedLogs
            }
        );
        setSocket(socket);
        setCurrentStep(index);
        setLogs(LOGS_PLACEHOLDER);
        disconnectRealtimeService();
    }

    return (
        <Drawer
            title={
                <Space>
                    <PipelineStatusTag status={pipeline?.status} /> {props.title}
                </Space>
            }
            placement="bottom"
            height={drawerHeight}
            onClose={handleClose}
            open={props.visible}
            destroyOnClose={true}
            closable={false}
            extra={
                <Space>
                    <Button
                        icon={
                            drawerHeight === "100%" ? <ShrinkOutlined /> : <ExpandAltOutlined />
                        }
                        onClick={toggleDrawerHeight}
                    />
                    <Button
                        icon={<CloseOutlined />}
                        onClick={handleClose}
                    />
                </Space>
            }
            footer={props.footer}
        >
            {
                loading ? <Skeleton /> :
                    <>
                        {
                            props.errorMessage ?
                                <>
                                    <Alert
                                        message={
                                            <>
                                                <CloseCircleOutlined style={{ color: "red" }} /> {props.errorMessage}
                                            </>
                                        }
                                        type="error"
                                    />
                                    <br />
                                </> :
                                <Row>
                                    <Col span={4}>
                                        <Steps
                                            direction="vertical"
                                            className="position-fixed template-pipeline-steps"
                                            current={currentStep}
                                            onChange={handleSelectStep}
                                            items={
                                                pipeline?.steps.map((step) => (
                                                    {
                                                        title: step.name,
                                                        status: getStepStatus(step.status),
                                                        icon: getStepStatus(step.status) === "process" && <LoadingOutlined style={{ color: "#097BB5" }} />,
                                                        disabled: getStepStatus(step.status) === "wait",
                                                        description:
                                                            <small>
                                                                <span style={{ color: step.status === "FAILED" ? "red" : "black" }}>
                                                                    {getStepStatusForDisplay(step.status)}
                                                                </span>
                                                                {
                                                                    step.startDate &&
                                                                    <Badge
                                                                        color="lightgray"
                                                                        style={{ margin: "auto 8px" }}
                                                                        text={
                                                                            <small>
                                                                                {
                                                                                    moment.utc(
                                                                                        moment.duration(moment(step.stopDate).diff(moment(step.startDate))).as("milliseconds")
                                                                                    ).format("m[min] s[sec]")
                                                                                        .replace(/^0min /, "")
                                                                                        .replace(/^0sec$/, "")
                                                                                }
                                                                            </small>
                                                                        } />
                                                                }
                                                            </small>
                                                    }
                                                ))
                                            }
                                        />
                                    </Col>
                                    <Col span={20}>
                                        <Content className="content-logs">
                                            <div className="flex-justify-space-between flex-align-center">
                                                <h3 style={{ color: "white", margin: 0 }}>
                                                    {
                                                        currentStep !== undefined ?
                                                            pipeline?.steps[currentStep]?.name :
                                                            ""
                                                    }
                                                    <span style={{ color: "GrayText" }}>
                                                        &nbsp;Step
                                                    </span>
                                                </h3>
                                                <Space>
                                                    <MonitorOutlined style={{ color: "goldenrod", fontSize: 20 }} />
                                                    {
                                                        !showDetailedLogs &&
                                                        <span>
                                                            Missing important logs?
                                                        </span>
                                                    }
                                                    <Button type="link" className="no-padding" onClick={() => setShowDetailedLogs(!showDetailedLogs)}>
                                                        {
                                                            showDetailedLogs ? "Show pretty logs" : "Show detailed logs"
                                                        }
                                                    </Button>
                                                </Space>
                                            </div>
                                            <Divider style={{ borderColor: "var(--border-color-base)" }} />
                                            <code>
                                                <pre>
                                                    {
                                                        logs.split("\n").map((log, index) => {
                                                            const logTime = logTimestampRegex.exec(log);
                                                            const timestamp = logTime && logTime[1];
                                                            // color formatting if the log has a valid timestamp
                                                            return timestamp ?
                                                                <div key={index}>
                                                                    <span style={{ color: logTimestampColor }}>
                                                                        {/* display the timestamp */}
                                                                        [{
                                                                            moment(+timestamp!).utc()
                                                                                .add(localTimezoneOffset, 'm')
                                                                                .format("MMM DD, HH:mm:ss.SSS")
                                                                        }]
                                                                    </span>&nbsp;
                                                                    <span>
                                                                        <Ansi>
                                                                            {/* display the rest of the log */}
                                                                            {log.split(" ").slice(1).join(" ")}
                                                                        </Ansi>
                                                                    </span>
                                                                </div>
                                                                :
                                                                <div key={index}>
                                                                    <Ansi>
                                                                        {log}
                                                                    </Ansi>
                                                                </div>
                                                        })
                                                    }
                                                </pre>
                                            </code>
                                        </Content>
                                    </Col>
                                </Row>
                        }

                    </>
            }
        </Drawer>
    )
}